import { calculateValue } from 'checkout/selectors/payment/generic'

interface TicketViewOptions {
  isBookDates?: boolean
  orderItemId?: string,
  ticketValidation?: App.ExperienceCheckoutItemValidationState['string'],
  isAccomBundle?: boolean
}

/**
 * Iterate through the item in the cart and sum the price and memberPrice
 * @param cartTicketItem
 * @param ticket
 * @param options
 * @returns
 */
export function mapExperienceCartItemToSummaryViews(
  cartTicketItem: App.Checkout.ExperienceItemTicket,
  ticket: App.ExperienceItemTicket | undefined,
  options: TicketViewOptions = {},
): App.Checkout.ExperienceItemTicketSummaryView | undefined {
  const { ticketId, count } = cartTicketItem

  // Bundled tickets are cart-priced: the cart snapshot is authoritative, so a failed
  // slot-ticket lookup must not zero the price -- the parent-row fold would then drop
  // the charge from the displayed total while payment charges it. The reachable case is
  // slots present but OUR ticket absent from them: e.g. the customer's per-customer
  // purchase limit filters that one ticket out of availability while the offer's other
  // tickets keep the slots non-empty. (Fully empty slots never reach here -- the item
  // view isn't built at all and checkout blocks as not-ready.)
  const cartPricedFallback = options.isAccomBundle ? cartTicketItem : undefined
  const ticketPrice = ticket?.price ?? cartPricedFallback?.price ?? 0
  const ticketMemberPrice = ticket?.memberPrice ?? cartPricedFallback?.memberPrice ?? 0
  const isZeroPriced = options.isBookDates
  let price = isZeroPriced ? 0 : ticketPrice * cartTicketItem.count
  let memberPrice = isZeroPriced ? 0 : ticketMemberPrice * cartTicketItem.count

  if (cartTicketItem.loyaltyTransferDiscount && price) {
    // this is our special loyalty transfer discount, so make one of the items 'free'
    price -= ticketPrice
    memberPrice -= ticketMemberPrice
  }

  if (cartTicketItem.count === 0) {
    return undefined
  }

  const isTicketValid = options.ticketValidation ? options.ticketValidation.fetching || options.ticketValidation.isValid : true

  return {
    ticketId,
    name: ticket?.name ?? cartTicketItem.name,
    fareType: ticket?.fareType ?? '',
    price,
    memberPrice,
    value: calculateValue(price, ticket?.discounts.base.percentage ?? 0),
    discounts: {
      base: {
        amount: ticket?.discounts.base.amount ?? 0,
        percentage: ticket?.discounts.base.percentage ?? 0,
      },
      app: {
        amount: ticket?.discounts.app.amount ?? 0,
        percentage: ticket?.discounts.app.percentage ?? 0,
      },
    },
    count,
    unavailable: !ticket || !isTicketValid,
    purchaseLimit: ticket?.purchaseLimit ?? { min: 1, max: 9999, maxReason: 'AVAILABILITY' },
    taxesAndFees: (ticket?.taxesAndFees ?? 0) * count,
    participantsPerUnit: ticket?.participantsPerUnit ?? 1,
    numberOfAdults: ticket?.numberOfAdults,
    numberOfChildren: ticket?.numberOfChildren,
    date: ticket?.date,
    time: ticket?.time,
  }
}
